const paragraph = (text) => ({ type: 'paragraph', text });
const list = (items) => ({ type: 'list', items });
const subheading = (text) => ({ type: 'subheading', text });

const CONTACT_EMAIL = 'admin@polarace.gg';
const CONTACT_ADDRESS = '70 Post Road, Chicopee, Massachusetts, USA 01020';

const TOS_SECTIONS = [
  {
    heading: null,
    blocks: [
      paragraph(`Welcome to Polar Ace, operated by Space Ace Inc. ("Company", "we", "us", or "our").`),
      paragraph(`These Terms of Service ("Terms") govern your access to and use of our website, mobile applications, software, analytics tools, streaming integrations, creator tools, and related services (collectively, the "Platform").`),
      paragraph(`By accessing or using the Platform, you agree to these Terms.`),
      paragraph(`If you do not agree, you may not use the Platform.`),
    ],
  },
  {
    heading: '1. Eligibility',
    blocks: [
      paragraph(`You must be at least 18 years old to use the Platform.`),
      paragraph(`By using the Platform you represent that:`),
      list([
        `You have the legal capacity to enter into a binding agreement`,
        `Your use of the Platform complies with applicable laws`,
        `Any information you provide is accurate`,
      ]),
      paragraph(`We may suspend or terminate accounts that violate these requirements.`),
    ],
  },
  {
    heading: '2. Platform Description',
    blocks: [
      paragraph(`The Platform provides services that may include:`),
      list([
        `Creator accounts and profiles`,
        `Creator-to-creator collaboration tools`,
        `Live streaming and video integrations`,
        `Advertising and brand partnership opportunities`,
        `Analytics and performance tracking`,
        `Creator monetization tools`,
        `Creator software integrations and extensions`,
      ]),
      paragraph(`We may modify, add, or discontinue features at any time.`),
    ],
  },
  {
    heading: '3. User Accounts',
    blocks: [
      paragraph(`To access certain features you must create an account.`),
      paragraph(`You agree to:`),
      list([
        `Maintain the confidentiality of login credentials`,
        `Accept responsibility for activity under your account`,
        `Notify us immediately of unauthorized access`,
      ]),
      paragraph(`We may suspend or terminate accounts involved in abuse, fraud, or violations of these Terms.`),
    ],
  },
  {
    heading: '4. User Content',
    blocks: [
      paragraph(`Users may upload, stream, publish, or otherwise submit content ("User Content").`),
      paragraph(`You retain ownership of your content.`),
      paragraph(`By uploading content you grant the Company a worldwide, non-exclusive, royalty-free license to:`),
      list([
        `Host`,
        `Stream`,
        `Display`,
        `Reproduce`,
        `Distribute`,
        `Promote`,
      ]),
      paragraph(`such content for purposes of operating and promoting the Platform.`),
      paragraph(`You represent that:`),
      list([
        `You own the rights to the content`,
        `Your content does not violate laws`,
        `Your content does not infringe third-party rights`,
      ]),
    ],
  },
  {
    heading: '5. Prohibited Content',
    blocks: [
      paragraph(`Users may not upload or stream content that includes:`),
      list([
        `Illegal material`,
        `Copyright infringement`,
        `Harassment or threats`,
        `Hate speech or discrimination`,
        `Non-consensual explicit content`,
        `Malware or malicious software`,
        `Fraudulent or misleading advertising`,
      ]),
      paragraph(`The Company may remove content at its discretion.`),
    ],
  },
  {
    heading: '5.1 Account Suspension for Risk',
    blocks: [
      subheading(`Risk-Based Account Restrictions`),
      paragraph(`The Company may suspend, restrict, or terminate accounts or transactions at any time where reasonably necessary to:`),
      list([
        `Prevent fraud or financial loss`,
        `Comply with payment processor requirements`,
        `Investigate suspicious activity`,
      ]),
      paragraph(`Such actions may be taken without prior notice.`),
    ],
  },
  {
    heading: '6. Live Streaming Content & Moderation',
    blocks: [
      paragraph(`The Platform may enable users to broadcast live video, audio, or interactive content.`),
      paragraph(`Users are solely responsible for all content transmitted during live streams.`),
      paragraph(`The Company does not review all live streams in real time.`),
      paragraph(`By using live streaming features, you agree:`),
      list([
        `You will not stream unlawful or infringing content`,
        `You will comply with platform policies and applicable laws`,
        `You are responsible for moderating your stream and viewer interactions`,
      ]),
      paragraph(`We may remove or interrupt live streams at our discretion.`),
      paragraph(`The Company shall not be liable for user-generated live content broadcast through the Platform.`),
    ],
  },
  {
    heading: 'No Guarantee of Earnings',
    blocks: [
      paragraph(`The Company does not guarantee:`),
      list([
        `Revenue generation`,
        `Advertising performance`,
        `Viewer engagement`,
        `Creator earnings`,
      ]),
      paragraph(`All monetization outcomes depend on independent factors beyond the Company's control.`),
    ],
  },
  {
    heading: '7. Advertising, Analytics & Automated Systems',
    blocks: [
      paragraph(`The Platform may include advertising systems, analytics tools, and automated placement technologies, including machine learning or artificial intelligence.`),
      paragraph(`These systems may be used to:`),
      list([
        `Place advertisements alongside content`,
        `Analyze engagement and performance metrics`,
        `Provide insights to creators`,
        `Improve platform performance`,
      ]),
      paragraph(`By using the Platform, users agree that:`),
      list([
        `The Company may collect performance and engagement data`,
        `Ads may be automatically inserted into platform content`,
        `Analytics may be used to improve advertising effectiveness`,
      ]),
      paragraph(`Advertising and revenue opportunities are not guaranteed.`),
    ],
  },
  {
    heading: '7.1 Payment Processor Disclaimer',
    blocks: [
      subheading(`Payment Processing`),
      paragraph(`All payments are processed through third-party payment processors.`),
      paragraph(`The Company:`),
      list([
        `Does not store full payment card details`,
        `Is not responsible for payment processor errors, outages, or decisions`,
        `Is not liable for actions taken by payment processors, including holds, reversals, or account restrictions`,
      ]),
    ],
  },
  {
    heading: '8. Third-Party Streaming Integrations',
    blocks: [
      paragraph(`The Platform may integrate with or display content from third-party streaming services.`),
      paragraph(`These integrations may include:`),
      list([
        `Embedded video players`,
        `External stream feeds`,
        `Third-party analytics`,
        `Creator streaming tools`,
      ]),
      paragraph(`The Company does not control third-party platforms and is not responsible for:`),
      list([
        `Platform outages`,
        `API changes`,
        `Policy changes`,
        `Content hosted externally`,
      ]),
      paragraph(`Use of those services remains subject to their respective terms.`),
    ],
  },
  {
    heading: '8.1 Evidence of Service Delivery',
    blocks: [
      subheading(`Proof of Service Delivery`),
      paragraph(`The Company may maintain records of service delivery, including:`),
      list([
        `Content display logs`,
        `Transaction timestamps`,
        `Platform activity records`,
      ]),
      paragraph(`These records may be used as evidence in resolving disputes.`),
    ],
  },
  {
    heading: '8.2 YouTube & Twitch Data Usage',
    blocks: [
      paragraph(`The Platform may allow users to connect accounts from third-party services, including YouTube and Twitch, to enable live streaming, chat integration, and creator tools.`),
      subheading(`Data Collected`),
      paragraph(`When a user connects a YouTube or Twitch account, the Platform may collect only the following data:`),
      list([
        `Channel handle or username`,
        `Public chat messages sent through the platform`,
        `Live streaming status (e.g., whether the user is currently streaming)`,
      ]),
      subheading(`Use of Data`),
      paragraph(`This data is used solely to:`),
      list([
        `Display usernames within the Platform`,
        `Enable live chat and viewer interaction features`,
        `Support streaming-related functionality and integrations`,
      ]),
      subheading(`Data Restrictions`),
      paragraph(`The Company does not collect, access, or store any additional data from YouTube or Twitch, including:`),
      list([
        `Private account information`,
        `Email addresses`,
        `Login credentials`,
        `Non-public analytics or account data`,
      ]),
      subheading(`User Control & Disconnection`),
      paragraph(`Users may disconnect their YouTube or Twitch account at any time by:`),
      list([
        `Using account settings within the Platform, or`,
        `Revoking access directly through their YouTube or Twitch account settings`,
      ]),
      paragraph(`Once disconnected, the Platform will no longer receive new data from the linked account.`),
    ],
  },
  {
    heading: '9. OBS Extensions & Creator Tools',
    blocks: [
      paragraph(`The Platform may provide downloadable extensions, plugins, or creator tools that interact with broadcasting software.`),
      paragraph(`These tools may allow:`),
      list([
        `Overlay display`,
        `Advertisement delivery`,
        `Viewer engagement features`,
        `Analytics tracking`,
      ]),
      paragraph(`Users acknowledge:`),
      list([
        `Creator tools interact with third-party software environments`,
        `The Company does not control external broadcasting software`,
        `Performance issues may occur due to third-party software conflicts`,
      ]),
      paragraph(`Use of these tools is at the user's own risk.`),
    ],
  },
  {
    heading: '10. Platform Data & Metrics',
    blocks: [
      paragraph(`The Platform may provide metrics such as:`),
      list([
        `Viewer counts`,
        `Engagement data`,
        `Advertisement impressions`,
        `Creator performance analytics`,
      ]),
      paragraph(`These metrics are informational estimates and may vary due to:`),
      list([
        `Network conditions`,
        `Third-party integrations`,
        `Measurement methodologies`,
      ]),
      paragraph(`The Company does not guarantee perfect accuracy of platform analytics.`),
    ],
  },
  {
    heading: '11. Creator Revenue & Fraud Protection',
    blocks: [
      paragraph(`Creators participating in monetization programs must comply with platform policies.`),
      paragraph(`Prohibited conduct includes:`),
      list([
        `Artificially inflating views or engagement`,
        `Using bots or automated traffic`,
        `Manipulating advertising systems`,
        `Creating fraudulent accounts or interactions`,
      ]),
      paragraph(`The Company may:`),
      list([
        `Withhold payments`,
        `Reverse transactions`,
        `Suspend monetization privileges`,
        `Terminate accounts`,
      ]),
      paragraph(`if fraudulent activity is detected.`),
    ],
  },
  {
    heading: '12. Creator Collaboration & User Interactions',
    blocks: [
      paragraph(`The Platform may facilitate collaboration between users including:`),
      list([
        `Creator partnerships`,
        `Brand deals`,
        `Joint projects`,
      ]),
      paragraph(`The Company does not guarantee the conduct of other users.`),
      paragraph(`Users are solely responsible for agreements or transactions they enter into with other users.`),
      paragraph(`The Company is not responsible for:`),
      list([
        `Contract disputes between creators`,
        `Payments between users`,
        `Business relationships formed through the Platform`,
      ]),
    ],
  },
  {
    heading: '13. Algorithmic Recommendations & Platform Visibility',
    blocks: [
      paragraph(`The Platform may use automated systems to determine:`),
      list([
        `Content recommendations`,
        `Creator discovery`,
        `Advertising placement`,
        `Search results and ranking`,
      ]),
      paragraph(`These systems may use data including engagement, viewer behavior, and platform activity.`),
      paragraph(`The Company does not guarantee visibility, promotion, or discovery for any user or content.`),
      paragraph(`Algorithmic systems may change at any time to improve platform functionality.`),
    ],
  },
  {
    heading: '14. Payments, Monetization & Transactions',
    blocks: [
      paragraph(`Creators may earn revenue through:`),
      list([
        `Advertising programs`,
        `Brand collaborations`,
        `Viewer payments`,
        `Other monetization tools`,
      ]),
      paragraph(`Payment terms may include:`),
      list([
        `Minimum payout thresholds`,
        `Processing delays`,
        `Payment processor requirements`,
      ]),
      paragraph(`Creators are responsible for paying applicable taxes.`),
    ],
  },
  {
    heading: '14.1 Final Sale / No Refunds',
    blocks: [
      paragraph(`All payments made on the Platform, including but not limited to digital goods, tips, donations, advertising placements, and promotional features, are final and non-refundable.`),
      paragraph(`By completing a transaction, you acknowledge and agree that:`),
      list([
        `You are receiving immediate access to digital services and/or exposure`,
        `Such services may be delivered instantly or near-instantly`,
        `You waive any right to refunds to the maximum extent permitted by applicable law`,
      ]),
    ],
  },
  {
    heading: '14.2 Unauthorized Transactions / Stolen Payment Methods',
    blocks: [
      paragraph(`You represent and warrant that you are authorized to use any payment method submitted on the Platform.`),
      paragraph(`If a transaction is made using a stolen, fraudulent, or unauthorized payment method:`),
      list([
        `The transaction may be reversed through the applicable payment network`,
      ]),
      paragraph(`The Company may immediately:`),
      list([
        `Suspend or terminate associated accounts`,
        `Revoke access to services or features obtained through the transaction`,
        `Remove any content, advertising, or placements associated with the transaction`,
      ]),
      paragraph(`Unauthorized use of payment methods may result in legal action.`),
    ],
  },
  {
    heading: '14.3 Chargebacks & Payment Disputes',
    blocks: [
      paragraph(`By using the Platform, you agree:`),
      list([
        `Not to initiate chargebacks or disputes except in cases of legitimate unauthorized use`,
        `To contact the Company first to attempt resolution of any payment issue`,
      ]),
      paragraph(`If a chargeback or dispute is initiated:`),
      list([
        `The Company may suspend or permanently terminate your account`,
        `The Company may restrict future transactions`,
      ]),
      paragraph(`The Company reserves the right to recover any losses, fees, or damages incurred as a result of the dispute.`),
    ],
  },
  {
    heading: '14.4 Fraud Prevention & Transaction Monitoring',
    blocks: [
      paragraph(`The Company reserves the right to:`),
      list([
        `Monitor transactions for fraud, abuse, or suspicious activity`,
        `Use third-party payment processors and fraud detection systems`,
        `Delay, review, or cancel transactions deemed high-risk`,
        `Request identity verification or additional information`,
      ]),
    ],
  },
  {
    heading: '14.5 Service Fulfillment',
    blocks: [
      paragraph(`Due to the nature of digital services provided by the Platform:`),
      list([
        `Services may be fulfilled immediately upon purchase`,
        `Fulfillment may include content display, advertisement placement, stream integration, or feature activation`,
        `Once initiated or delivered, services are considered fully performed`,
      ]),
    ],
  },
  {
    heading: '14.6 Platform Discretion',
    blocks: [
      paragraph(`The Company reserves the sole right to:`),
      list([
        `Refuse, cancel, or reverse any transaction at any time`,
        `Issue refunds only at its sole discretion in exceptional circumstances`,
      ]),
    ],
  },
  {
    heading: '14.7 Taxes & Payment Processor Requirements',
    blocks: [
      paragraph(`Users and creators are responsible for:`),
      list([
        `Paying all applicable taxes related to transactions`,
        `Complying with the terms of any third-party payment processors used by the Platform`,
      ]),
    ],
  },
  {
    heading: '14.8 Creator Payout Clawback & Adjustments',
    blocks: [
      paragraph(`The Company reserves the right to withhold, reverse, offset, or reclaim any payments made or payable to creators in connection with the Platform under the following circumstances:`),
      subheading(`(a) Fraudulent or Invalid Activity`),
      paragraph(`If revenue is generated through:`),
      list([
        `Unauthorized transactions (including stolen or fraudulent payment methods)`,
        `Chargebacks or payment disputes`,
        `Artificially inflated engagement (including bots, fake traffic, or manipulation)`,
        `Any violation of these Terms`,
      ]),
      paragraph(`The Company may:`),
      list([
        `Reverse associated earnings`,
        `Deduct such amounts from current or future payouts`,
        `Require repayment of funds already disbursed`,
      ]),
      subheading(`(b) Chargebacks & Payment Reversals`),
      paragraph(`If a transaction tied to creator earnings is later reversed, refunded, or disputed:`),
      list([
        `The corresponding earnings are deemed invalid`,
      ]),
      paragraph(`The Company may:`),
      list([
        `Deduct the amount from pending payouts`,
        `Offset the amount against future earnings`,
        `Invoice the creator for repayment if necessary`,
      ]),
      subheading(`(c) Holds & Delayed Payouts`),
      paragraph(`The Company may place temporary holds on creator earnings where reasonably necessary to:`),
      list([
        `Investigate suspicious activity`,
        `Comply with payment processor requirements`,
        `Mitigate risk of fraud or financial loss`,
      ]),
      subheading(`(d) Negative Balances`),
      paragraph(`If a creator's account balance becomes negative:`),
      list([
        `The creator remains liable`,
      ]),
      paragraph(`The Company may:`),
      list([
        `Offset future earnings`,
        `Suspend monetization features`,
        `Pursue recovery of outstanding amounts`,
      ]),
      subheading(`(e) Enforcement & Account Actions`),
      paragraph(`The Company may:`),
      list([
        `Suspend or terminate accounts`,
        `Revoke monetization access`,
        `Withhold unpaid earnings`,
      ]),
    ],
  },
  {
    heading: '14.9 Authorization of Charges',
    blocks: [
      paragraph(`By providing a payment method, you authorize the Company and its payment processors to:`),
      list([
        `Charge your payment method for all transactions initiated on the Platform`,
        `Store and process payment credentials as necessary`,
        `Process future transactions initiated by you or on your behalf`,
      ]),
      paragraph(`You agree that all payment obligations are binding at the time of transaction authorization.`),
    ],
  },
  {
    heading: '14.10 Digital Goods & Immediate Performance Acknowledgment',
    blocks: [
      paragraph(`You acknowledge that:`),
      list([
        `The Platform provides digital goods and services`,
        `Access and/or delivery may occur immediately upon purchase`,
        `By completing a transaction, you expressly consent to immediate performance`,
        `You waive any right to cancel or withdraw from the transaction once performance has begun`,
      ]),
    ],
  },
  {
    heading: '15. Copyright & DMCA',
    blocks: [
      paragraph(`If you believe content on the Platform infringes copyright, you may submit a DMCA notice to:`),
      paragraph(`Email: ${CONTACT_EMAIL}`),
      paragraph(`Address: ${CONTACT_ADDRESS}`),
      paragraph(`We may remove infringing content and terminate repeat infringers.`),
    ],
  },
  {
    heading: '16. Platform Security & Acceptable Use',
    blocks: [
      paragraph(`Users may not:`),
      list([
        `Attempt to hack or reverse engineer the Platform`,
        `Use automated bots without authorization`,
        `Scrape platform data`,
        `Interfere with platform security systems`,
      ]),
      paragraph(`Violations may result in termination.`),
    ],
  },
  {
    heading: '17. Termination',
    blocks: [
      paragraph(`We may suspend or terminate accounts if users:`),
      list([
        `Violate these Terms`,
        `Engage in illegal activity`,
        `Create risk or liability for the Company`,
      ]),
      paragraph(`Users may stop using the Platform at any time.`),
    ],
  },
  {
    heading: '18. Disclaimer of Warranties',
    blocks: [
      paragraph(`The Platform is provided "AS IS" and "AS AVAILABLE."`),
      paragraph(`We do not guarantee that:`),
      list([
        `The Platform will always be available`,
        `Services will be uninterrupted`,
        `Content will be accurate`,
      ]),
    ],
  },
  {
    heading: '19. Limitation of Liability',
    blocks: [
      paragraph(`To the fullest extent permitted by law, the Company is not liable for:`),
      list([
        `Indirect damages`,
        `Loss of profits or data`,
        `Content posted by users`,
        `Third-party platform actions`,
        `Chargebacks, fraudulent transactions, or unauthorized payments`,
        `Loss of revenue, business opportunities, or advertising performance`,
        `Creator earnings, expected income, or monetization outcomes`,
        `Platform misuse by other users`,
      ]),
      paragraph(`The Platform acts solely as a technology provider and does not guarantee financial results.`),
      paragraph(`Total liability shall not exceed amounts paid by the user in the previous 12 months.`),
    ],
  },
  {
    heading: '20. Indemnification',
    blocks: [
      paragraph(`You agree to indemnify and defend the Company against claims arising from:`),
      list([
        `Your use of the Platform`,
        `Your content`,
        `Your violation of these Terms`,
      ]),
    ],
  },
  {
    heading: '20.1 Indemnification (continued)',
    blocks: [
      paragraph(`You agree to indemnify and hold harmless the Company from any claims arising from:`),
      list([
        `Your use of any payment method`,
        `Chargebacks, disputes, or payment reversals initiated by you`,
        `Your content, transactions, or monetization activity`,
        `Any violation of payment processor rules`,
      ]),
    ],
  },
  {
    heading: '21. Governing Law',
    blocks: [
      paragraph(`These Terms are governed by the laws of the State of Delaware.`),
    ],
  },
  {
    heading: '22. Arbitration',
    blocks: [
      paragraph(`Disputes will be resolved through binding arbitration, except where prohibited by law.`),
    ],
  },
  {
    heading: '23. Changes to Terms',
    blocks: [
      paragraph(`We may update these Terms at any time.`),
      paragraph(`Continued use of the Platform constitutes acceptance of the updated Terms.`),
    ],
  },
  {
    heading: '24. Contact',
    blocks: [
      paragraph(`Company: Space Ace Inc. dba Polar Ace`),
      paragraph(`Email: ${CONTACT_EMAIL}`),
      paragraph(`Address: ${CONTACT_ADDRESS}`),
    ],
  },
];

const PRIVACY_SECTIONS = [
  {
    heading: null,
    blocks: [
      paragraph(`This Privacy Policy explains how Space Ace Inc. dba Polar Ace ("Company", "we", "us", or "our") collects, uses, and protects information when you use Polar Ace (the "Platform").`),
      paragraph(`By using the Platform, you consent to the practices described in this policy.`),
    ],
  },
  {
    heading: '1. Information We Collect',
    blocks: [
      paragraph(`We may collect the following types of information.`),
      subheading(`Account Information`),
      paragraph(`When you create an account we may collect:`),
      list([
        `Name`,
        `Email address`,
        `Username`,
        `Profile information`,
        `Password (stored securely)`,
      ]),
      subheading(`Creator & Platform Usage Information`),
      paragraph(`We may collect information related to your activity on the Platform, including:`),
      list([
        `Content you upload or stream`,
        `Engagement metrics (views, likes, interactions)`,
        `Collaboration activity`,
        `Analytics and performance metrics`,
      ]),
      subheading(`Device and Technical Information`),
      paragraph(`When you use the Platform we may collect:`),
      list([
        `IP address`,
        `Device type`,
        `Operating system`,
        `Browser type`,
        `Network information`,
        `Crash and diagnostic data`,
      ]),
      subheading(`Payment Information`),
      paragraph(`If monetization features are used, payment processing may be handled by third-party processors (Stripe Inc.).`),
      paragraph(`We may collect:`),
      list([
        `Payment account identifiers`,
        `Transaction history`,
        `Payout information`,
      ]),
      paragraph(`Payment details may be processed and stored by third-party payment providers.`),
      subheading(`Third-Party Platform Data (YouTube & Twitch)`),
      paragraph(`PolarAce may allow users to connect their accounts from third-party platforms, including YouTube and Twitch, to enable certain features.`),
      subheading(`Data Collected`),
      paragraph(`When a user connects a YouTube or Twitch account, PolarAce may collect the following limited data:`),
      list([
        `Channel handle / username`,
        `Public chat messages sent by the user`,
        `Live streaming status (e.g., whether the user is currently streaming live)`,
      ]),
      subheading(`Use of Data`),
      paragraph(`This data is used solely to:`),
      list([
        `Display the user's username within the PolarAce platform`,
        `Enable live chat and interactive features`,
        `Support functionality related to live streaming and fan interaction`,
      ]),
      subheading(`Data Limitations`),
      paragraph(`PolarAce does not collect, access, or store any additional YouTube or Twitch data beyond what is explicitly listed above. This includes, but is not limited to:`),
      list([
        `Private account information`,
        `Email addresses`,
        `Account credentials`,
        `Non-public analytics or metadata`,
      ]),
      subheading(`Disconnecting Accounts`),
      paragraph(`Users may disconnect their YouTube or Twitch account at any time by:`),
      list([
        `Using the account settings within PolarAce, or`,
        `Revoking access directly through their YouTube or Twitch account settings`,
      ]),
      paragraph(`Once disconnected, PolarAce will no longer receive new data from the linked account.`),
    ],
  },
  {
    heading: '2. How We Use Information',
    blocks: [
      paragraph(`We use information to:`),
      list([
        `Provide and operate the Platform`,
        `Enable creator collaboration features`,
        `Provide analytics and insights`,
        `Deliver advertising services`,
        `Improve platform functionality`,
        `Prevent fraud and abuse`,
        `Maintain platform security`,
        `Communicate with users`,
      ]),
    ],
  },
  {
    heading: '3. Advertising & Analytics',
    blocks: [
      paragraph(`The Platform may use data to support:`),
      list([
        `Advertising placement`,
        `Performance measurement`,
        `Audience analytics`,
        `Creator insights`,
      ]),
      paragraph(`Analytics may include engagement statistics and viewer behavior.`),
      paragraph(`Some analytics systems may use automated or machine-learning technologies.`),
    ],
  },
  {
    heading: '4. Cookies and Tracking Technologies',
    blocks: [
      paragraph(`We may use cookies or similar technologies to:`),
      list([
        `Maintain login sessions`,
        `Analyze usage patterns`,
        `Improve performance`,
        `Personalize user experience`,
      ]),
      paragraph(`Users may adjust browser settings to disable cookies, though some features may not function properly.`),
    ],
  },
  {
    heading: '5. Sharing of Information',
    blocks: [
      paragraph(`We may share information with:`),
      subheading(`Service Providers`),
      paragraph(`Companies that assist in operating the Platform, including:`),
      list([
        `Cloud hosting providers`,
        `Payment processors`,
        `Analytics services`,
        `Security and fraud prevention services`,
      ]),
      subheading(`Legal Compliance`),
      paragraph(`We may disclose information when required to:`),
      list([
        `Comply with legal obligations`,
        `Respond to lawful requests`,
        `Protect rights or safety`,
      ]),
      subheading(`Business Transfers`),
      paragraph(`If the Company is involved in a merger, acquisition, or sale of assets, user information may be transferred.`),
    ],
  },
  {
    heading: '6. Data Retention',
    blocks: [
      paragraph(`We retain information as long as necessary to:`),
      list([
        `Provide services`,
        `Maintain platform functionality`,
        `Comply with legal obligations`,
      ]),
      paragraph(`Users may request deletion of their account information where permitted by law.`),
    ],
  },
  {
    heading: '7. Data Security',
    blocks: [
      paragraph(`We use reasonable security measures to protect information, including:`),
      list([
        `Encryption`,
        `Access controls`,
        `Secure infrastructure`,
      ]),
      paragraph(`However, no system can guarantee absolute security.`),
    ],
  },
  {
    heading: '8. International Users',
    blocks: [
      paragraph(`If you access the Platform from outside the United States, your information may be transferred to and processed in the United States or other jurisdictions.`),
    ],
  },
  {
    heading: `9. Children's Privacy`,
    blocks: [
      paragraph(`The Platform is not intended for users under the age of 18.`),
      paragraph(`We do not knowingly collect personal information from children.`),
    ],
  },
  {
    heading: '10. Your Privacy Rights',
    blocks: [
      paragraph(`Depending on your location, you may have rights including:`),
      list([
        `Access to your data`,
        `Request correction of information`,
        `Request deletion of data`,
      ]),
      paragraph(`Requests may be submitted to the contact address below.`),
    ],
  },
  {
    heading: '11. Changes to This Policy',
    blocks: [
      paragraph(`We may update this Privacy Policy periodically.`),
      paragraph(`The updated version will include a revised "Last Updated" date.`),
    ],
  },
  {
    heading: '12. Contact',
    blocks: [
      paragraph(`For privacy-related questions:`),
      paragraph(`Company: Space Ace Inc. dba Polar Ace`),
      paragraph(`Email: ${CONTACT_EMAIL}`),
      paragraph(`Address: ${CONTACT_ADDRESS}`),
    ],
  },
];

const GUIDELINES_SECTIONS = [
  {
    heading: null,
    blocks: [
      paragraph(`These Community Guidelines outline expected behavior on Polar Ace. Our goal is to create a safe, fun, and comfortable environment for creators, viewers, and their audience. We work hard to build and maintain a specific and friendly environment and culture in which we believe is the most entertaining for our community. We ask all of our users to acknowledge and respect the culture, make an effort to be friendly, treat people how you want to be treated, and make meaningful contributions to our community.`),
    ],
  },
  {
    heading: '1. Respectful Conduct',
    blocks: [
      paragraph(`Users must treat others respectfully. Prohibited behavior includes harassment or bullying, threats or intimidation, targeted harassment campaigns, and hate speech or discrimination.`),
    ],
  },
  {
    heading: '2. Illegal Content',
    blocks: [
      paragraph(`Content that violates laws is prohibited, including illegal activity, copyright infringement, fraud or scams, and distribution of illegal goods.`),
    ],
  },
  {
    heading: '3. Safety and Harmful Behavior',
    blocks: [
      paragraph(`Content that promotes or depicts harmful behavior is not allowed. Examples include encouraging violence, threats of physical harm, and dangerous activities likely to cause injury.`),
    ],
  },
  {
    heading: '4. Adult Content',
    blocks: [
      paragraph(`Explicit or pornographic content is strictly prohibited. Content involving minors in any sexual context is strictly prohibited. Violations may be reported to authorities.`),
    ],
  },
  {
    heading: '5. Intellectual Property',
    blocks: [
      paragraph(`Users must respect intellectual property rights. You may not upload or stream content that violates copyright, uses copyrighted material without permission, or misuses trademarks. Copyright holders may submit DMCA takedown requests.`),
    ],
  },
  {
    heading: '6. Platform Integrity',
    blocks: [
      paragraph(`Users may not manipulate the Platform through bots or automated accounts, fake engagement, artificially inflating views, or fraudulent advertising interactions. Accounts involved in such activity may be suspended.`),
    ],
  },
  {
    heading: '7. Spam and Deceptive Practices',
    blocks: [
      paragraph(`The following are prohibited: spam messages, misleading promotions, fake accounts, and phishing attempts.`),
    ],
  },
  {
    heading: '8. Creator Responsibility',
    blocks: [
      paragraph(`Creators are responsible for the content they produce and distribute. Creators should ensure content complies with laws, respect audience safety, and follow advertising disclosure requirements.`),
    ],
  },
  {
    heading: '9. Enforcement',
    blocks: [
      paragraph(`Violations may result in content removal, account suspension, monetization removal, or permanent bans. Serious violations may be reported to appropriate authorities.`),
    ],
  },
  {
    heading: '10. Reporting Violations',
    blocks: [
      paragraph(`Users may report violations through platform reporting tools or by contacting: Email: ${CONTACT_EMAIL}. Reports may be reviewed by moderation teams.`),
    ],
  },
  {
    heading: '11. Policy Updates',
    blocks: [
      paragraph(`We may update these guidelines to reflect changes in the Platform or legal requirements.`),
    ],
  },
];

export const LEGAL_TAB_ORDER = ['terms', 'privacy', 'guidelines'];

export const LEGAL_CONTENT = {
  terms: {
    key: 'terms',
    label: 'Terms of Service',
    lastUpdated: null,
    sections: TOS_SECTIONS,
  },
  privacy: {
    key: 'privacy',
    label: 'Privacy Policy',
    lastUpdated: null,
    sections: PRIVACY_SECTIONS,
  },
  guidelines: {
    key: 'guidelines',
    label: 'Community Guidelines',
    lastUpdated: '3/23/2026',
    sections: GUIDELINES_SECTIONS,
  },
};
